import { supabase } from './supabase'

/**
 * 156 (David, 11 Oct): the hub's video card waits for the announcement.
 *
 * `hub_video_state()` says when the card is shown to staff, whether it is
 * shown now, and whether the reader may change that (content.publish — the
 * people who write the notices). Before the time, a scheduler still sees the
 * card under a strip saying it is hidden and until when: that is how David
 * checks it plays before anybody else can.
 *
 * PRESENTATION, NOT ACCESS CONTROL. The Vimeo id is in the public bundle
 * (lib/video.ts); what protects the video is the login and Vimeo's domain
 * allow-list. This decides the moment, not the secret.
 *
 * FAILS CLOSED in Home: until the state is read, and if the read fails, the
 * card is not drawn. Showing a video early is the failure David asked to
 * prevent; a missing card for the length of a retry is not.
 */
export type VideoGate = {
  /** UTC instant, or null for "no time set — shown". */
  visibleFrom: Date | null
  /** True when staff see it now. */
  shown: boolean
  /** The reader holds content.publish unscoped. */
  canSchedule: boolean
}

type Row = { visible_from: string | null; shown: boolean; can_schedule: boolean }

export function shapeVideoGate(rows: Row[] | null): VideoGate {
  const r = rows?.[0]
  // No row at all is not possible from the function (it always returns one),
  // but a tenant nobody configured means "shown", the same as null.
  if (!r) return { visibleFrom: null, shown: true, canSchedule: false }
  return {
    visibleFrom: r.visible_from ? new Date(r.visible_from) : null,
    shown: r.shown,
    canSchedule: r.can_schedule,
  }
}

export async function loadVideoGate(): Promise<VideoGate> {
  const { data, error } = await supabase.rpc('hub_video_state')
  if (error) throw error
  return shapeVideoGate(data as Row[] | null)
}

/** null = show it now. */
export async function setVideoVisibleFrom(at: Date | null): Promise<void> {
  const { error } = await supabase.rpc('set_hub_video_visible_from', {
    p_at: at ? at.toISOString() : null,
  })
  if (error) throw error
}

/** Whether THIS reader gets the card: staff when shown, schedulers always. */
export function videoCardVisible(gate: VideoGate | null): boolean {
  return gate !== null && (gate.shown || gate.canSchedule)
}

/** A Date as the value a <input type="datetime-local"> wants, in local time. */
export function toLocalInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Back again. An empty or unparseable value is null, never "now". */
export function fromLocalInputValue(v: string): Date | null {
  if (!v) return null
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}
